import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Plus, Search, Edit, Trash2, ChevronLeft, ChevronRight, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Debounce hook for search
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
};

export const Products = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const perPage = 50;
  
  // Debounce search query to avoid too many API calls
  const debouncedSearch = useDebounce(searchQuery, 300);

  const fetchProducts = useCallback(async (page = 1, search = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString()
      });
      if (search.trim()) {
        params.append('search', search.trim());
      }
      
      const response = await axios.get(`${API}/products?${params.toString()}`);
      setProducts(response.data.products);
      setTotalProducts(response.data.total);
      setTotalPages(response.data.total_pages);
      setCurrentPage(response.data.page);
    } catch (error) {
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch products when page or search changes
  useEffect(() => {
    fetchProducts(currentPage, debouncedSearch);
  }, [fetchProducts, currentPage, debouncedSearch]);

  // Reset to page 1 when search changes
  useEffect(() => {
    if (debouncedSearch !== '') {
      setCurrentPage(1);
    }
  }, [debouncedSearch]);

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      await axios.delete(`${API}/products/by-id/${productId}`);
      toast.success('Product deleted successfully');
      fetchProducts(currentPage, debouncedSearch);
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setCurrentPage(1);
  };

  const canEdit = user?.role === 'admin' || user?.role === 'sales';

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="products-title">
              Products
            </h1>
            <p className="mt-1 text-sm sm:text-base lg:text-lg text-slate-600" style={{ fontFamily: 'Public Sans, sans-serif' }}>
              {totalProducts > 0 ? `${totalProducts} products in catalog` : 'Manage your product catalog'}
            </p>
          </div>
          {canEdit && (
            <Link
              to="/products/new"
              data-testid="create-product-button"
              className="inline-flex items-center justify-center w-full sm:w-auto px-4 py-2.5 bg-slate-900 text-white rounded-md hover:bg-slate-800 font-medium transition-all active:scale-[0.98] text-sm sm:text-base"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Product
            </Link>
          )}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search by design number, name, or color..."
            data-testid="search-products-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-10 py-2.5 sm:py-3 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base"
          />
          {searchQuery && (
            <button
              onClick={clearSearch}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Search indicator */}
        {debouncedSearch && !loading && (
          <div className="text-sm text-slate-600">
            Showing {totalProducts} result{totalProducts !== 1 ? 's' : ''} for &ldquo;<span className="font-medium">{debouncedSearch}</span>&rdquo;
          </div>
        )}

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-slate-900 mx-auto"></div>
            <p className="mt-4 text-sm sm:text-base text-slate-600">Loading products...</p>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12 bg-white border border-slate-200 rounded-lg">
            <p className="text-slate-600">{debouncedSearch ? `No products found for "${debouncedSearch}"` : 'No products found'}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6" data-testid="products-grid">
            {products.map((product) => (
              <div
                key={product.design_number}
                data-testid={`product-card-${product.design_number}`}
                className="bg-white border border-slate-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden"
              >
                <div className="aspect-square bg-slate-100 overflow-hidden">
                  <img
                    src={product.primary_image}
                    alt={product.product_name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-3 sm:p-4">
                  <div className="mb-2">
                    <h3 className="font-semibold text-slate-900 text-sm sm:text-base line-clamp-1">{product.product_name}</h3>
                    <p className="text-xs sm:text-sm text-slate-500">#{product.design_number}</p>
                  </div>
                  <div className="space-y-1 text-xs sm:text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Bulk:</span>
                      <span className="font-medium text-slate-900">₹{product.pricing.bulk}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Set-wise:</span>
                      <span className="font-medium text-slate-900">₹{product.pricing.setwise}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Reselling:</span>
                      <span className="font-medium text-slate-900">₹{product.pricing.reselling}</span>
                    </div>
                  </div>
                  {canEdit && (
                    <div className="mt-3 sm:mt-4 flex gap-2">
                      <Link
                        to={`/products/edit/${product.id}`}
                        data-testid={`edit-product-${product.design_number}`}
                        className="flex-1 inline-flex items-center justify-center px-2 sm:px-3 py-1.5 sm:py-2 bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200 text-xs sm:text-sm font-medium transition-colors"
                      >
                        <Edit className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                        Edit
                      </Link>
                      {user?.role === 'admin' && (
                        <button
                          onClick={() => handleDelete(product.id)}
                          data-testid={`delete-product-${product.design_number}`}
                          className="px-2 sm:px-3 py-1.5 sm:py-2 bg-rose-50 text-rose-600 rounded-md hover:bg-rose-100 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-slate-200">
            <div className="text-sm text-slate-600">
              Showing {((currentPage - 1) * perPage) + 1} - {Math.min(currentPage * perPage, totalProducts)} of {totalProducts} products
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-2 rounded-md border ${currentPage === 1 ? 'border-slate-200 text-slate-300 cursor-not-allowed' : 'border-slate-300 text-slate-700 hover:bg-slate-100'}`}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              
              {/* Page Numbers */}
              <div className="flex items-center gap-1">
                {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = idx + 1;
                  } else if (currentPage <= 3) {
                    pageNum = idx + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + idx;
                  } else {
                    pageNum = currentPage - 2 + idx;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`w-10 h-10 rounded-md text-sm font-medium ${
                        currentPage === pageNum
                          ? 'bg-indigo-600 text-white'
                          : 'border border-slate-300 text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-md border ${currentPage === totalPages ? 'border-slate-200 text-slate-300 cursor-not-allowed' : 'border-slate-300 text-slate-700 hover:bg-slate-100'}`}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};
